import { generateCustomerId } from "@loanlink-nl/migration";
import type {
	InventarisationAssets,
	InventarisationCustomer,
	InventarisationIncome,
	InventarisationLiabilities,
	PartialCustomer,
} from "@loanlink-nl/model-app";

/**
 * Creates a new Inventarisation customer with minimal data.
 */
export function newCustomer(): InventarisationCustomer {
	return {
		id: generateCustomerId(),
		personDetails: {},
		contactDetails: {},
		otherDetails: {
			birthCountry: "NL Nederland",
			nationality: "NL Nederland",
		},
		residence: {
			country: "NL Nederland",
		},
		identification: {
			issueCountry: "NL Nederland",
		},
	};
}

/**
 * Removes all inventarisation category entries (income, assets, liabilities)
 * that belong to the given customer id, in place.
 *
 * Entries are only removed when they actually exist for that customer, so
 * calling this for a customer without category data is a no-op. This prevents
 * "ghost" records from lingering in `byCustomer` after a customer is removed.
 */
export function removeCustomerCategoryEntries(
	categories: {
		income: InventarisationIncome;
		assets: InventarisationAssets;
		liabilities: InventarisationLiabilities;
	},
	customerId: string,
): void {
	const collections = [
		categories.income.byCustomer,
		categories.assets.byCustomer,
		categories.liabilities.byCustomer,
	];

	for (const byCustomer of collections) {
		for (let index = byCustomer.length - 1; index >= 0; index--) {
			if (byCustomer[index]?.customerId === customerId) {
				byCustomer.splice(index, 1);
			}
		}
	}
}

/**
 * Get appropriate icon for customer.
 */
function getCustomerIcon(customer: PartialCustomer) {
	const fallbackIcon = "face";
	if (customer.personDetails === undefined) {
		return fallbackIcon;
	}
	const genderToIconMap: Record<
		Exclude<InventarisationCustomer["personDetails"]["gender"], undefined>,
		string
	> = {
		"01 M": "face_6",
		"02 V": "face_3",
		"03 X": fallbackIcon,
	};
	const icon = customer.personDetails.gender
		? genderToIconMap[customer.personDetails.gender]
		: undefined;
	return icon === undefined ? fallbackIcon : icon;
}

/**
 * Get saved name for customer.
 * Uses firstName if available, otherwise falls back to initials.
 * Null if no name is present.
 */
export function getCustomerName(customer?: PartialCustomer) {
	if (!customer) {
		return null;
	}
	if (customer.personDetails === undefined) {
		return null;
	}

	let name: string | null = "";
	if (customer.personDetails.firstName) {
		name += `${customer.personDetails.firstName} `;
	} else if (customer.personDetails.initials) {
		name += `${customer.personDetails.initials} `;
	}
	if (customer.personDetails.infix) {
		name += `${customer.personDetails.infix} `;
	}
	if (customer.personDetails.lastName) {
		name += customer.personDetails.lastName;
	}

	if (name === "") {
		name = null;
	}

	return name;
}

/**
 * Get short or full label for customer.
 * Provides a presentable fallback if name is undefined.
 */
export function getCustomerLabel(
	customers: InventarisationCustomer[],
	id: string,
	short = false,
) {
	const getFallbackLabel = (id: string) => {
		const index = customers.findIndex((entry) => id === entry.id);
		return `Klant #${index + 1}`;
	};

	const customer = customers.find((entry) => entry.id === id);
	const name = getCustomerName(customer);

	if (name === null) {
		return getFallbackLabel(id);
	}

	if (short && customer?.personDetails.firstName) {
		return customer.personDetails.firstName;
	}

	return name;
}

/**
 * Convert a list of customers to select options.
 */
export function getCustomerSelectOptions(customers: InventarisationCustomer[]) {
	interface Option {
		icon?: string;
		label: string;
		value: string;
	}
	return customers.map((customer) => ({
		icon: getCustomerIcon(customer),
		label: getCustomerLabel(customers, customer.id),
		value: customer.id,
	})) as Option[];
}

/**
 * Get combined display name for all customers.
 * Returns names joined with " & " (e.g. "John Doe & Jane Doe").
 * Returns null if no customers have names.
 */
export function getCombinedCustomerNames(
	customers: InventarisationCustomer[],
): string | null {
	const names = customers
		.map((customer) => getCustomerName(customer))
		.filter((name): name is string => name !== null)
		.map((name) => name.trim());

	if (names.length === 0) {
		return null;
	}

	return names.join(" & ");
}

/**
 * Gets the intitials of the customer first names.
 */
export function getCustomerInitials(customer?: PartialCustomer) {
	const details = customer?.personDetails;
	if (details?.firstName === undefined) {
		return;
	}
	return details.firstName
		.split(" ")
		.map((part) => part.trim())
		.filter((part) => part !== "")
		.map((part) => `${part[0]?.toLocaleUpperCase()}.`)
		.join("");
}
